<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import { store, requestSearchFocus, isViewingOtherArchive, returnToLaunchArchive } from "./store.js";
import { formatBytes } from "./format.js";

const route = useRoute();

// True when the user has dived into a group from GroupManagementView that isn't
// this session's launch context — the header then offers a one-tap way back.
const viewingOther = computed(() => isViewingOtherArchive());

/**
 * Bottom navigation tabs. `match` decides active state — exact for the root
 * tab (so /trash doesn't light up คลัง), prefix for sections with subroutes.
 */
const tabs = [
  { to: "/", icon: "inventory_2", label: "คลัง", match: (p: string) => p === "/" },
  { to: "/trash", icon: "delete", label: "ถังขยะ", match: (p: string) => p === "/trash" },
  { to: "/plans", icon: "analytics", label: "แพ็กเกจ", match: (p: string) => p === "/plans" },
  { to: "/settings", icon: "settings", label: "ตั้งค่า", match: (p: string) => p.startsWith("/settings") },
] as const;

// The header search icon only does anything on screens that have a FilterBar to focus.
const showSearchIcon = computed(() => route.path === "/" || route.path === "/trash");

/**
 * "Focus" routes (meta.chrome === "focus") are single-task screens — currently
 * the report form. They keep the wordmark bar for orientation but drop the
 * quota meter and the bottom nav: the quota is irrelevant to the task, and a
 * fixed bottom nav both steals the space the on-screen keyboard needs and sits
 * where the screen's own primary action belongs.
 */
const focusChrome = computed(() => route.meta.chrome === "focus");

const quotaPct = computed(() => {
  if (!store.archive) return 0;
  const used = Number(store.archive.usedBytes);
  const quota = Number(store.archive.quotaBytes);
  return quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
});
</script>

<template>
  <div class="flex min-h-screen flex-col bg-surface font-body-md text-on-surface">
    <header class="sticky top-0 z-40 border-b border-outline-variant bg-surface/90 backdrop-blur-md">
      <div class="flex h-16 items-center justify-between gap-3 px-container-margin">
        <div class="min-w-0">
          <span class="block font-headline-lg text-[20px] font-bold leading-tight tracking-tight text-primary">BarkUp</span>
          <!-- Which archive is this? Matters most when arriving via a group's
               ?gid= link — the same app shows a different archive per context. -->
          <span v-if="store.archive" class="flex items-center gap-1 font-metadata text-on-surface-variant">
            <span class="material-symbols-outlined shrink-0 text-[14px]">{{
              store.archive.ownerType === "GROUP" ? "groups" : "person"
            }}</span>
            <span class="truncate">{{
              store.archive.ownerType === "GROUP" ? (store.archive.groupName ?? "คลังกลุ่ม") : "คลังส่วนตัว"
            }}</span>
            <!-- Only when standing in a group the user hopped into from
                 GroupManagementView — a quick exit back to their launch archive. -->
            <button
              v-if="viewingOther"
              type="button"
              class="ml-1 flex shrink-0 items-center gap-0.5 rounded-full bg-surface-container px-2 py-0.5 font-label-caps text-[10px] text-on-surface transition-colors hover:bg-surface-container-high"
              @click="returnToLaunchArchive"
            >
              <span class="material-symbols-outlined text-[12px]">arrow_back</span>
              กลับคลังของฉัน
            </button>
          </span>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button
            v-if="showSearchIcon"
            type="button"
            aria-label="ค้นหา"
            class="flex h-10 w-10 items-center justify-center text-on-surface-variant transition-colors hover:text-on-surface"
            @click="requestSearchFocus"
          >
            <span class="material-symbols-outlined text-[22px]">search</span>
          </button>
          <div class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-outline-variant bg-surface-container">
            <img
              v-if="store.profile?.pictureUrl"
              :src="store.profile.pictureUrl"
              :alt="store.profile.displayName"
              class="h-full w-full object-cover"
            />
            <span v-else class="material-symbols-outlined text-[18px] text-on-surface-variant">person</span>
          </div>
        </div>
      </div>

      <div v-if="store.archive && !focusChrome" class="px-container-margin pb-3">
        <div class="mb-2 flex items-end justify-between">
          <span class="font-label-caps uppercase tracking-wider text-on-surface-variant">พื้นที่ใช้งาน</span>
          <span class="font-metadata text-on-surface-variant">
            <span class="font-bold text-on-surface">{{ formatBytes(store.archive.usedBytes) }}</span>
            / {{ formatBytes(store.archive.quotaBytes) }}
          </span>
        </div>
        <div class="h-[6px] w-full overflow-hidden rounded-full bg-surface-container-high">
          <div class="h-full rounded-full bg-primary transition-all duration-500" :style="{ width: quotaPct + '%' }" />
        </div>
      </div>

      <div
        v-if="store.archive?.frozenAt"
        class="mx-container-margin mb-3 rounded-xl border border-error-container bg-error-container/70 px-3 py-2.5"
      >
        <p class="font-body-sm text-on-error-container">
          ⚠️ บอทถูกนำออกจากกลุ่มนี้ — คลังนี้จะถูกลบถาวรใน 30 วัน หากไม่ได้เพิ่มบอทกลับเข้ากลุ่ม
        </p>
      </div>
    </header>

    <main class="flex flex-1 flex-col px-container-margin pt-stack-md" :class="focusChrome ? 'pb-0' : 'pb-[96px]'">
      <div v-if="store.loading" class="py-16 text-center font-body-sm text-on-surface-variant">กำลังเชื่อมต่อ...</div>
      <div v-else-if="store.error" class="py-16 text-center font-body-sm text-error">{{ store.error }}</div>
      <router-view v-else />
    </main>

    <!-- M3-style navigation bar (the pattern Drive/Gmail mobile use): the active
         tab gets a soft pill indicator behind its icon — a shape signal, not just
         a color shift, so the current tab is findable at a glance. -->
    <nav
      v-if="!focusChrome"
      class="safe-pb fixed bottom-0 left-0 z-50 flex w-full items-start border-t border-outline-variant bg-surface pb-2 pt-2"
      aria-label="เมนูหลัก"
    >
      <router-link
        v-for="tab in tabs"
        :key="tab.to"
        :to="tab.to"
        class="flex min-h-[56px] w-1/4 flex-col items-center gap-1"
        :aria-current="tab.match(route.path) ? 'page' : undefined"
      >
        <span
          class="flex h-8 w-16 items-center justify-center rounded-full transition-colors duration-200"
          :class="tab.match(route.path) ? 'bg-primary-container' : ''"
        >
          <span
            class="material-symbols-outlined text-[22px] transition-colors duration-200"
            :class="tab.match(route.path) ? 'text-primary' : 'text-on-surface-variant'"
            :style="tab.match(route.path) ? { fontVariationSettings: `'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 20` } : {}"
            >{{ tab.icon }}</span
          >
        </span>
        <span
          class="font-label-caps text-[10px] transition-colors duration-200"
          :class="tab.match(route.path) ? 'font-bold text-on-surface' : 'text-on-surface-variant'"
          >{{ tab.label }}</span
        >
      </router-link>
    </nav>
  </div>
</template>
